import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Search, Plus } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import api from '../../api/clients';

interface Category {
  id: number;
  nombre: string;
  descripcion: string | null;
  created_at: string;
}

export function CategoryList() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [formData, setFormData] = useState({ nombre: '', descripcion: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Cargar categorías
  const loadCategories = async () => {
    try {
      const response = await api.get('/categories');
      if (response.data.success) {
        setCategories(response.data.data);
      }
    } catch (error) {
      toast.error('Error al cargar categorías');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  const filteredCategories = categories.filter(cat =>
    cat.nombre.toLowerCase().includes(search.toLowerCase()) ||
    (cat.descripcion?.toLowerCase().includes(search.toLowerCase()) || false)
  );

  // Abrir modal para crear
  const handleNew = () => {
    setEditingCategory(null);
    setFormData({ nombre: '', descripcion: '' });
    setIsModalOpen(true);
  };

  // Abrir modal para editar
  const handleEdit = (category: Category) => {
    setEditingCategory(category);
    setFormData({ nombre: category.nombre, descripcion: category.descripcion || '' });
    setIsModalOpen(true);
  };

  // Guardar categoría
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (editingCategory) {
        const response = await api.put(`/categories/${editingCategory.id}`, formData);
        if (response.data.success) {
          toast.success('Categoría actualizada correctamente');
          loadCategories();
          setIsModalOpen(false);
        }
      } else {
        const response = await api.post('/categories', formData);
        if (response.data.success) {
          toast.success('Categoría creada correctamente');
          loadCategories();
          setIsModalOpen(false);
        }
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al guardar la categoría');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Eliminar categoría
  const handleDelete = async (category: Category) => {
    if (!confirm(`¿Estás seguro de eliminar la categoría "${category.nombre}"?`)) return;

    try {
      const response = await api.delete(`/categories/${category.id}`);
      if (response.data.success) {
        toast.success('Categoría eliminada correctamente');
        loadCategories();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al eliminar la categoría');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Categorías</h1>
          <p className="text-sm text-gray-500">
            {filteredCategories.length} categoría(s) registradas
          </p>
        </div>
        <Button onClick={handleNew} className="bg-blue-600 hover:bg-blue-700 text-white">
          <Plus size={18} />
          Nueva Categoría
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
        <Input
          type="text"
          placeholder="Buscar categorías por nombre o descripción..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      <Card className="p-6">
        {filteredCategories.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            {search ? 'No se encontraron categorías' : 'No hay categorías creadas. ¡Crea tu primera categoría!'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-gray-600">ID</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-600">Nombre</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-600">Descripción</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-600">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredCategories.map((category) => (
                  <tr key={category.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                    <td className="py-3 px-4 text-gray-700">{category.id}</td>
                    <td className="py-3 px-4 font-medium text-gray-800">{category.nombre}</td>
                    <td className="py-3 px-4 text-gray-600">{category.descripcion || '-'}</td>
                    <td className="py-3 px-4 space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(category)}
                        className="text-blue-600 border-blue-300 hover:bg-blue-50"
                      >
                        Editar
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(category)}
                        className="text-red-600 border-red-300 hover:bg-red-50"
                      >
                        Eliminar
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Modal de crear/editar */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingCategory ? 'Editar Categoría' : 'Nueva Categoría'}
      >
        <form onSubmit={handleSave} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nombre *
            </label>
            <Input
              type="text"
              value={formData.nombre}
              onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
              placeholder="Ej: Electrodomésticos"
              required
              className="w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descripción
            </label>
            <textarea
              value={formData.descripcion}
              onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
              placeholder="Descripción de la categoría..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsModalOpen(false)}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !formData.nombre.trim()}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isSubmitting ? 'Guardando...' : editingCategory ? 'Actualizar' : 'Crear'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}